import { Action, createReducer, on } from '@ngrx/store';
import { DocAdminPermissions } from 'src/app/compliance/models/doc-admin-permissions.model';
import { DocumentType } from 'src/app/compliance/models/document-type';
import { DocumentTypeCategory } from 'src/app/compliance/models/document-type-category.model';
import { DocumentTypes } from 'src/app/compliance/models/document-types.model';
import { DocumentTypeSummaryBase } from 'src/app/shared/lib/compliance-shared/models/document-type-summary-base.model';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { DocumentTypeActions } from './actions';
import { resetCurrentDocument } from './actions/doc-admin-details.actions';
import { DocAdminRequirementPermissionsDto } from 'src/app/compliance/api/models/doc-admin-requirement-permissions-dto.model';
import { FacilityTypeDto } from 'src/app/compliance/api/models/facility-type-dto.model';
import { WorkTypeSettingsDto } from 'src/app/compliance/api/models/work-type-settings-dto.model';
import { LocumsContractTypeSettingsDto } from 'src/app/compliance/api/models/locums-contract-type-settings-dto.model';
import { ManualReviewCandidateTypeDto } from 'src/app/compliance/api/models/manual-review-candidate-type-dto.model';

export const documentTypesFeatureKey = 'document-types';

export interface DocumentTypesState {
    initFacilityLookup: KeyValue<number, string>;
    facilityLookups: KeyValue<number, string>[];
    initAssociationLookup: KeyValue<number, string>;
    associationLookups: KeyValue<number, string>[];
    partnerNetworkTaskTypes: KeyValue<number, string>[];
    candidateTaskTypes: KeyValue<number, string>[];
    specialityLookups: KeyValue<number, string>[];
    stateLookups: KeyValue<number, string>[];
    facilityTypeLookups: FacilityTypeDto[];
    manualReviewCandidateTypeSettingLookups: ManualReviewCandidateTypeDto[];
    workTypeSettingLookups: WorkTypeSettingsDto[];
    locumsContractTypeSettingLookups: LocumsContractTypeSettingsDto[];
    currentDocument: DocumentTypes;
    documentSummary: PagingTokenFixed<DocumentTypeSummaryBase[]>;
    documentTypeCategories: DocumentTypeCategory[];
    documentTypeCategoriesList: string[];
    filter: FetchArgs;
    error: any;
    documentTypesList: DocumentType[];
    documentType: DocumentType;
    permissions: DocAdminPermissions;
    requirementPermissions: DocAdminRequirementPermissionsDto;
    linesOfBusinessLookups: KeyValue<number, string>[];
    requirementLevelsLookups: KeyValue<number, string>[];
    isOcrEnabled: boolean;
    isOcrToggleSuccessful: boolean;
    isSaving: boolean;
    previousNavigationUrl: string;
    currentDocumentLevel: number;
    currentDocumentId: number;
}

export const initialState: DocumentTypesState = {
    initFacilityLookup: null,
    facilityLookups: [],
    initAssociationLookup: null,
    associationLookups: [],
    candidateTaskTypes: [],
    partnerNetworkTaskTypes: [],
    specialityLookups: [],
    stateLookups: [],
    facilityTypeLookups: [],
    manualReviewCandidateTypeSettingLookups: [],
    workTypeSettingLookups: [],
    locumsContractTypeSettingLookups: [],
    documentType: null,
    currentDocument: null,
    documentSummary: null,
    documentTypeCategories: [],
    documentTypeCategoriesList: [],
    filter: {
        filter: {},
        sort: { field: 'id', dir: 'asc' },
        skip: 0,
        pageSize: 25
    },
    error: null,
    documentTypesList: [],
    permissions: null,
    requirementPermissions: null,
    linesOfBusinessLookups: [],
    requirementLevelsLookups: [],
    isOcrEnabled: null,
    isOcrToggleSuccessful: null,
    isSaving: false,
    previousNavigationUrl: '',
    currentDocumentLevel: null,
    currentDocumentId: null
};

export const initialDocument: DocumentTypes = {
    id: 0,
    name: '',
    documentSuffix: '',
    qmDefault: false,
    document: null,
    hospQmRequirementId: 0,
    internalDescription: '',
    externalNoteId: null,
    externalNote: '',
    internalDescriptionId: null,
    externalInstructions: '',
    externalInstructionsId: null,
    facilityId: 0,
    facilityName: '',
    qmAssociationId: 0,
    associationName: '',
    stateId: 0,
    stateName: '',
    documentId: 0,
    candidateTaskTypeId: 0,
    candidateTaskTypeName: '',
    partnerNetworkTaskTypeId: null,
    isCandidateUploadAllowed: false,
    fileName: null,
    pageCount: 0,
    documentTypeCategoryId: 0,
    esignatureTemplates: [],
    requiresExpiration: false,
    states: [],
    snapshotStates: [],
    facilityTypes: [],
    snapshotFacilityTypes: [],
    manualReviewCandidateTypeSettings: [],
    snapshotManualReviewCandidateTypeSettings: [],
    workTypeSettings: [],
    snapshotWorkTypeSettings: [],
    locumsContractTypeSettings: [],
    snapshotLocumsContractTypeSettings: [],
    emailTo: '',
    emailBody: '',
    emailNew: false,
    emailComplianceLiaison: false,
    isValidationRequired: true,
    requirementLevelId: 0,
    acceptanceCriteriaType: 0,
    acceptanceCriteriaStatus: 0,
    updatedDate: null,
    updatedBy: '',
    documentTypeRuleExpressionId: null,
    hasAcceptanceCriteriaConflicts: null,
    documentTypeRuleExpressionConflictGroupId: null,
    isEsignatureEnvelopeAutosendEnabled: true,
    calculatedExpirationDateEnabled: false,
    manualReviewProfessionSettings: null,
    candidateReviewDocumentId: null,
    candidateReviewFileName: null,
    candidateReviewPageCount: null
};

const documentTypesReducer = createReducer(
    initialState,
    on(DocumentTypeActions.DocumentTypesDetail.getPermissionsSuccess, (state, action) => ({
        ...state,
        permissions: action.permissions,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.getRequirementPermissionsSuccess, (state, action) => ({
        ...state,
        requirementPermissions: action.permissions,
        error: null
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupSuccess, (state, action) => ({
        ...state,
        initFacilityLookup: action.facility,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsSuccess, (state, action) => ({
        ...state,
        facilityLookups: action.facilities,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsSuccess, (state, action) => ({
        ...state,
        requirementLevelsLookups: action.requirementLevels,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookupSoccess, (state, action) => ({
        ...state,
        initAssociationLookup: action.association,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsSuccess, (state, action) => ({
        ...state,
        associationLookups: action.associations,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearFacilityLookups, (state, action) => ({
        ...state,
        facilityLookups: [],
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearAssociationLookups, (state, action) => ({
        ...state,
        associationLookups: [],
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearInitAssociationLookups, (state, action) => ({
        ...state,
        initAssociationLookup: null,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearInitFacilityLookups, (state, action) => ({
        ...state,
        initFacilityLookup: null,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsSuccess, (state, action) => ({
        ...state,
        specialityLookups: action.specialties,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getStateLookupsSuccess, (state, action) => ({
        ...state,
        stateLookups: action.states,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsSuccess, (state, action) => ({
        ...state,
        facilityTypeLookups: action.facilityTypes,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsSuccess, (state, action) => ({
        ...state,
        manualReviewCandidateTypeSettingLookups: action.manualReviewTypes,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsSuccess, (state, action) => ({
        ...state,
        workTypeSettingLookups: action.workTypeSettings,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsSuccess, (state, action) => ({
        ...state,
        locumsContractTypeSettingLookups: action.locumsContractTypeSettings,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookupsSuccess, (state, action) => ({
        ...state,
        linesOfBusinessLookups: action.linesOfBusiness,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesSuccess, (state, action) => ({
        ...state,
        candidateTaskTypes: action.candidateTaskTypes,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesSuccess, (state, action) => ({
        ...state,
        partnerNetworkTaskTypes: action.partnerNetworkTaskTypes,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesSuccess, (state, action) => ({
        ...state,
        documentTypeCategories: action.documentTypeCatories,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesListSuccess, (state, action) => ({
        ...state,
        documentTypeCategoriesList: action.documentTypeCategoriesList,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearDocumentTypeCategories, (state, action) => ({
        ...state,
        documentTypeCategories: null,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.setCurrentDocumentLevel, (state, action) => ({
        ...state,
        currentDocumentLevel: action.documentTypeLevel,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearCurrentDocumentLevel, (state, action) => ({
        ...state,
        currentDocumentLevel: null,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.setDocumentTypesFilter, (state, action) => ({
        ...state,
        filter: {
            ...action.filter,
            filter: Object.keys(action.filter.filter)
                .filter((k) => !isEmpty(action.filter.filter[k]))
                .reduce((a, k) => ({ ...a, [k]: action.filter.filter[k] }), {})
        },

        documentSummary: null,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.getDocumentTypesSuccess, (state, action) => ({
        ...state,
        documentSummary: action.documents,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.getDocumentTypesListSuccess, (state, action) => ({
        ...state,
        documentTypesList: action.documentTypesList
    })),

    on(DocumentTypeActions.DocumentTypes.getDocumentTypeSuccess, (state, action) => ({
        ...state,
        documentType: action.documentType
    })),

    on(DocumentTypeActions.DocumentTypes.getDocumentTypesListFailure, (state, action) => ({
        ...state,
        documentTypesList: null,
        error: action.error
    })),
    on(
        DocumentTypeActions.DocumentTypesDetail.updateDocument,
        DocumentTypeActions.DocumentTypesDetail.uploadDocument,
        DocumentTypeActions.DocumentTypesDetail.disableDocument,
        DocumentTypeActions.DocumentTypesDetail.enableDocument,
        (state, action) => ({
            ...state,
            isSaving: true
        })
    ),
    on(
        DocumentTypeActions.DocumentTypesDetail.updateDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.uploadDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.getDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.initializeDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.removeRequirementSuccess,
        DocumentTypeActions.DocumentTypesDetail.createNewDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.uploadDocumentFileSuccess,
        DocumentTypeActions.DocumentTypesDetail.uploadCandidateReviewDocumentFileSuccess,
        DocumentTypeActions.DocumentTypesDetail.disableDocumentSuccess,
        DocumentTypeActions.DocumentTypesDetail.enableDocumentSuccess,
        (state, action) => ({
            ...state,
            currentDocument: action.document,
            isSaving: false,
            error: ''
        })
    ),
    on(
        DocumentTypeActions.DocumentTypesDetail.createNewDocument,
        DocumentTypeActions.DocumentTypesDetail.updateDocument,
        DocumentTypeActions.DocumentTypesDetail.getDocument,
        DocumentTypeActions.DocumentTypesDetail.initializeDocument,
        (state, action) => ({
            ...state,
            exampleDoc: null
        })
    ),
    on(DocumentTypeActions.DocumentTypes.getOcrEnabledStatusSuccess, (state, action) => ({
        ...state,
        isOcrEnabled: action.isOcrEnabled,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.toggleOcrEnabledSuccess, (state, action) => ({
        ...state,
        isOcrToggleSuccessful: action.isOcrToggleSuccessful,
        isOcrEnabled: !state.isOcrEnabled,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.toggleOcrEnabledFailure, (state, action) => ({
        ...state,
        isOcrToggleSuccessful: false,
        error: ''
    })),
    on(DocumentTypeActions.DocumentTypes.setPreviousNavigationUrl, (state, action) => ({
        ...state,
        previousNavigationUrl: action.previousNavigationUrl
    })),
    on(DocumentTypeActions.DocumentTypesDetail.clearRequirementLevelLookups, (state, action) => ({
        ...state,
        requirementLevelsLookups: null
    })),
    on(
        DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupFailure,
        DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookupFailure,
        DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesFailure,
        DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesFailure,
        DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getStateLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsFailure,
        DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesFailure,
        DocumentTypeActions.DocumentTypesDetail.uploadDocumentFailure,
        DocumentTypeActions.DocumentTypesDetail.uploadCandidateReviewDocumentFileFailure,
        DocumentTypeActions.DocumentTypesDetail.updateDocumentFailure,
        DocumentTypeActions.DocumentTypesDetail.disableDocumentFailure,
        DocumentTypeActions.DocumentTypesDetail.enableDocumentFailure,
        DocumentTypeActions.DocumentTypesDetail.createNewDocumentFailure,
        DocumentTypeActions.DocumentTypesDetail.removeRequirementFailure,
        DocumentTypeActions.DocumentTypesDetail.removeRequirementFailure,
        DocumentTypeActions.DocumentTypes.getDocumentTypesFailure,
        DocumentTypeActions.DocumentTypesDetail.getPermissionsFailure,
        DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsFailure,
        (state, action) => ({
            ...state,
            error: action.error
        })
    ),

    on(resetCurrentDocument, (state, action) => ({
        ...state,
        currentDocument: null
    }))
);

export function reducer(state: DocumentTypesState | undefined, action: Action) {
    return documentTypesReducer(state, action);
}
